import { Link, useLocation, useParams } from "react-router-dom";
import { projects, findProjectBySlug } from "../data/projects";

export default function Header() {
  const location = useLocation();
  const params = useParams();

  // Determine if we're on a project sub-page. If so, HOME must route back to "/"
  // AND restore the snap-scroll position to the originating project's frame.
  const isCaseStudy = location.pathname.startsWith("/project/");
  const slug = params.slug;
  const projectIndex = slug ? findProjectBySlug(slug) : -1;
  const project = projectIndex >= 0 ? projects[projectIndex] : null;
  const sectionId =
    projectIndex >= 0
      ? `section-${String(projectIndex + 1).padStart(2, "0")}`
      : null;

  // Last section anchor for the "Contact" link is no longer needed — Contact
  // now opens an email to luca@lucaworks.com. Kept for reference.

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 w-full z-50 px-6 md:px-10 py-6 md:py-7 mix-blend-difference text-white pointer-events-none"
    >
      <div className="flex justify-between items-center">
        <div aria-hidden />

        <nav className="pointer-events-auto flex items-center gap-7 md:gap-10 font-sans text-[10px] md:text-[11px] tracking-[0.28em] uppercase">
          {isCaseStudy && sectionId ? (
            <Link
              to="/"
              state={{
                scrollToSectionId: sectionId,
                image: project?.transitionImage
              }}
              data-testid="nav-home"
              className="hover:opacity-60 transition-opacity duration-500"
            >
              Home
            </Link>
          ) : (
            <a
              href="#section-01"
              data-testid="nav-home"
              onClick={(e) => {
                // iOS Safari's snap engine is aggressive: even with snap
                // disabled programmatically, it can pull the viewport back
                // to the originating section. After 3 iterations of fighting
                // it with snap-type and overflow tricks, the only reliable
                // fix is to make section-01 the ONLY valid snap target on
                // the page. We do this by stripping `scroll-snap-align`
                // from every section except section-01 right before the
                // scroll, then restoring after a short delay.
                e.preventDefault();
                const html = document.documentElement;
                const sections = document.querySelectorAll(".portfolio-snap");
                const stripped = [];

                sections.forEach((s) => {
                  if (s.id === "section-01") return;
                  stripped.push({ el: s });
                  s.style.scrollSnapAlign = "none";
                  s.style.scrollSnapStop = "normal";
                });

                const prevBehavior = html.style.scrollBehavior;
                html.style.scrollBehavior = "auto";

                window.scrollTo(0, 0);
                html.scrollTop = 0;
                document.body.scrollTop = 0;

                // Re-zero on next frame in case iOS dropped the first call.
                requestAnimationFrame(() => {
                  window.scrollTo(0, 0);
                });

                // Restore each section's snap rules after iOS has settled.
                // 600ms is long enough for any iOS Safari momentum to die.
                // We ALWAYS removeProperty (never restore an inline value),
                // because the original snap rules live in the CSS class
                // `.portfolio-snap` — there is never anything to restore.
                // This also defeats the rapid double-tap race: a 2nd handler
                // that fires while the 1st restore is pending will read the
                // already-stripped inline 'none' value, but since both
                // restores call removeProperty unconditionally, the end
                // state is always clean.
                setTimeout(() => {
                  stripped.forEach(({ el }) => {
                    el.style.removeProperty("scroll-snap-align");
                    el.style.removeProperty("scroll-snap-stop");
                  });
                  html.style.scrollBehavior = prevBehavior;
                }, 600);
              }}
              className="hover:opacity-60 transition-opacity duration-500"
            >
              Home
            </a>
          )}

          {/* BIO — routes to project-01's sub-page. Because /project/:slug
              lives inside the same React Router tree as / (the homepage),
              we use the same seamless cross-fade transition the section
              click uses. `state.image` primes the transition overlay so
              the current view fades to the sub-page's hero, matching the
              in-app "click a project" flow. */}
          <Link
            to="/project/project-01"
            state={{ image: projects[0]?.transitionImage }}
            data-testid="nav-bio"
            className="hover:opacity-60 transition-opacity duration-500"
          >
            Bio
          </Link>

          <a
            href={process.env.REACT_APP_CV_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cv"
            className="hover:opacity-60 transition-opacity duration-500"
          >
            CV
          </a>

          <a
            href="mailto:luca@lucaworks.com"
            data-testid="nav-contact"
            className="hover:opacity-60 transition-opacity duration-500"
          >
            Contact
          </a>
        </nav>
      </div>
    </header>
  );
}
